import useHasAccess from "@hooks/useHasAccess"
import useIsDesktop from "@hooks/useIsDesktop"
import useSlugExtractor from "@hooks/useSlugExtractor"
import useUser from "@hooks/useUser"

import { AccessCheck } from "@particles"

import { NavTopBar, ObjectsView, ViewBar, ViewMenu } from "@organisms"
import CardsView from "@organisms/ObjectsView/CardsView/CardsView"
import TableView from "@organisms/ObjectsView/TableView/TableView"
import ViewTypesProvider from "@organisms/ObjectsView/ViewTypesProvider/ViewTypesProvider"

import { AppFrame } from "@templates"

import { VENDOR_ROUTES } from "@routes/vendor"

import { objectMap } from "@constants/objectMap"
import { PERMISSION_LEVEL } from "@constants/permissionLevel"
import {
    VENDORS_LIST_CONTROLS_ACTIVE_TAB,
    VENDORS_LIST_CONTROLS_KEYWORD_SEARCH,
    VENDORS_LIST_CONTROLS_PHASE,
    VENDORS_LIST_CONTROLS_VIEW,
} from "@constants/storage"

import { VENDOR_CARDS_VIEW_ATTRIBUTE_OPTIONS, VENDOR_CARDS_VIEW_SORT_OPTIONS } from "./VendorList.constants"
import { VendorObjectsViewTabs } from "./VendorObjectsViewTabs"
import VendorCardsView from "./views/VendorCardsView/VendorCardsView"
import VendorTableView from "./views/VendorTableView/VendorTableView"

export default function VendorList() {
    const isDesktop = useIsDesktop()
    const { hasPermission } = useHasAccess()
    const [createRoute] = useSlugExtractor([VENDOR_ROUTES.CREATE])
    const { user } = useUser()

    const hasVendorCreditsFeature = !!user?.service_company?.feature_vendor_credits_enabled
    const vendorCreditFields: string[] = ["unapplied_credit", "amount_owed"]
    const cardsViewAttributes = VENDOR_CARDS_VIEW_ATTRIBUTE_OPTIONS.filter(
        (option) => hasVendorCreditsFeature || !vendorCreditFields.includes(option.name),
    )
    const cardsViewSortOptions = VENDOR_CARDS_VIEW_SORT_OPTIONS.filter(
        (option) => hasVendorCreditsFeature || !vendorCreditFields.includes(option.name),
    )

    return (
        <AppFrame.Root collaborationRoomName="vendor-list">
            <ViewTypesProvider
                viewTypes={["table", "cards"]}
                viewTypeKey={VENDORS_LIST_CONTROLS_VIEW}
                defaultViewType={isDesktop ? "table" : "cards"}
            >
                {({ activeViewType }) => (
                    <>
                        <NavTopBar.Root>
                            <NavTopBar.Header>
                                <NavTopBar.NavTopBarHeaderStart>
                                    <NavTopBar.PageTitle label={objectMap.vendor.labelPlural} />
                                </NavTopBar.NavTopBarHeaderStart>
                                <NavTopBar.NavTopBarHeaderEnd>
                                    <NavTopBar.PagePresence />
                                    <AccessCheck
                                        permissions={{
                                            vendors_list_permission: PERMISSION_LEVEL.RESTRICTED,
                                        }}
                                    >
                                        <AccessCheck
                                            permissions={{
                                                vendors_view_permission: PERMISSION_LEVEL.FULL,
                                            }}
                                        >
                                            <NavTopBar.SearchButton scope="Vendors" />
                                        </AccessCheck>
                                    </AccessCheck>
                                    <AccessCheck
                                        permissions={{
                                            vendors_create_permission: PERMISSION_LEVEL.RESTRICTED,
                                        }}
                                    >
                                        <NavTopBar.CTAButton destination={createRoute} />
                                    </AccessCheck>
                                </NavTopBar.NavTopBarHeaderEnd>
                            </NavTopBar.Header>
                        </NavTopBar.Root>
                        <ObjectsView.Provider
                            activePhaseStorageKey={VENDORS_LIST_CONTROLS_PHASE}
                            activeTabStorageKey={VENDORS_LIST_CONTROLS_ACTIVE_TAB}
                            searchKeywordsStorageKey={VENDORS_LIST_CONTROLS_KEYWORD_SEARCH}
                            listTitle="Vendors"
                            objectType="vendor"
                            objectIndexName="Vendor"
                            objectName="Vendor"
                            defaultActivePhase="active"
                            objectCreateRoute={createRoute}
                            isRowClickable={hasPermission("vendors_view_permission", PERMISSION_LEVEL.FULL)}
                            isEmptyStateCreateButtonVisible={hasPermission(
                                "vendors_create_permission",
                                PERMISSION_LEVEL.FULL,
                            )}
                        >
                            <TableView.Provider>
                                <CardsView.Provider
                                    attributes={cardsViewAttributes}
                                    sortOptions={cardsViewSortOptions}
                                >
                                    <ViewBar.Root>
                                        <ViewBar.Start>
                                            <ViewBar.Phases>
                                                <ObjectsView.Phase phase="active" title="Active" />
                                                <ObjectsView.Phase phase="inactive" title="Inactive" />
                                            </ViewBar.Phases>
                                            <ViewBar.VerticalDivider />
                                            <VendorObjectsViewTabs />
                                        </ViewBar.Start>
                                        <ViewBar.End>
                                            <ObjectsView.SearchInput />
                                            <ViewMenu />
                                        </ViewBar.End>
                                    </ViewBar.Root>
                                    {activeViewType === "table" && <VendorTableView />}
                                    {activeViewType === "cards" && <VendorCardsView />}
                                </CardsView.Provider>
                            </TableView.Provider>
                        </ObjectsView.Provider>
                    </>
                )}
            </ViewTypesProvider>
        </AppFrame.Root>
    )
}
